import bbox from '@turf/bbox';
import { Geometry } from 'geojson';
import { atom, useAtomValue } from 'jotai';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import type { MapRef } from 'react-map-gl';
import { Map, PolygonArea } from 'components/map';
import { mapColors } from 'components/map/map-colors';
import { LINE_STYLES } from 'components/map/map-styles';
import { Line, Point } from 'components/map/well';
import { buildPolygonStyle, findCenter } from 'lib/map';
import { scopedMapConfigAtom } from '../atoms';
import {
  horizontalUnitsLayerAtom,
  horizontalWellsLayerAtom,
  subtractLayerAtom,
  unitAllocationTractsLayerAtom,
  verticalUnitsLayerAtom,
  verticalWellsLayerAtom,
} from './atoms';
import { ScopedCrmLayerSelection } from './map-layer-controls';
import styles from './scoped-map-panel.module.scss';

/** One row of the optional green/red threshold overlay. */
export interface ThresholdOverlayItem {
  id: string;
  geometry: Geometry;
  isAboveThreshold: boolean;
}

/** Threshold overlay: a list of unit- or UAT-shaped polygons, each above-or-below. */
export interface ThresholdOverlay {
  type: 'unit' | 'uat';
  items: ThresholdOverlayItem[];
}

/**
 * One renderable wellbore for a slot. The `feature` is whatever the BE
 * shipped (`WellFeaturesResponse.survey | .stick | .surfaceHole | .bottomHole`),
 * so callers pass it through directly. LineStrings render as a Mapbox
 * line layer; Points render as a Marker dot.
 */
export interface ScopedMapWellFeature {
  uwi: string;
  feature: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.Point>;
}

/** A unit allocation tract polygon. */
export interface ScopedMapUatGeometry {
  id: number;
  geometry: Geometry;
}

interface ScopedMapPanelProps {
  /** Currently selected unit's polygon — drives baseline scoping/center. */
  selectedUnitGeometry?: GeoJSON.Polygon | null;
  /** Renderable well/slot features — black lines (or Marker dots for verticals); selected slot emphasized. */
  wellFeatures?: ScopedMapWellFeature[];
  /** UAT polygons — medium-blue fill, selected UAT emphasized. */
  uatGeometries?: ScopedMapUatGeometry[];
  /** Subtract polygon — yellow outline, no fill. */
  subtractGeometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  /** UWI of the slot row currently selected in the SlotList. */
  selectedSlotId?: string;
  /** id of the UAT row currently selected in the UatList. */
  selectedUatId?: number;
  /**
   * Optional threshold overlay layered ON TOP of the baseline. When
   * present, fitBounds includes all overlay items so the user sees the
   * full above/below picture.
   */
  thresholdOverlay?: ThresholdOverlay;
  /**
   * Optional content for the accordion dock at the bottom of the
   * panel (e.g. `<AllocationManagerAccordion>`). When omitted (Future
   * Development) no accordion is rendered.
   */
  allocationManager?: ReactNode;
}

interface CenterInput {
  selectedUnitGeometry: GeoJSON.Polygon | null | undefined;
  subtractGeometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null | undefined;
  uatGeometries: ScopedMapUatGeometry[] | undefined;
}

/**
 * Picks a center point for the Map component. Selection-driven: prefer
 * the selected unit, then the subtract polygon, then the first UAT.
 * Returns `null` when nothing is renderable so the caller can show a
 * placeholder instead of mounting an unscoped world map.
 *
 * @param {CenterInput} input - Geometries to consider, in priority order
 * @return {object | null} Center point or null when nothing is renderable
 */
const computeCenter = (
  input: CenterInput
): { longitude: number; latitude: number } | null => {
  const { selectedUnitGeometry, subtractGeometry, uatGeometries } = input;
  if (selectedUnitGeometry) return findCenter(selectedUnitGeometry);
  if (subtractGeometry) return findCenter(subtractGeometry);
  if (uatGeometries && uatGeometries.length > 0)
    return findCenter(uatGeometries[0].geometry);
  return null;
};

/** Inline darker-and-thicker line style for the currently-selected slot. */
const SELECTED_SLOT_LINE_STYLE = {
  'line-color': mapColors.crmScopedSelectedSlotLine,
  'line-width': 3,
};

/** Static paint for the selected unit polygon — light-blue fill, dark-blue outline. */
const SELECTED_UNIT_STYLE = buildPolygonStyle({
  color: mapColors.crmScopedSelectedUnitFill,
  fillOpacity: 0.3,
  lineColor: mapColors.crmScopedSelectedUnitLine,
  lineOpacity: 1,
  lineWidth: 2,
});

/** Static paint for the subtract polygon — yellow outline only. */
const SUBTRACT_STYLE = buildPolygonStyle({
  color: mapColors.crmScopedSubtractLine,
  fillOpacity: 0,
  lineColor: mapColors.crmScopedSubtractLine,
  lineOpacity: 1,
  lineWidth: 2,
});

/** Static paint for the currently-selected UAT polygon — emphasized. */
const UAT_SELECTED_STYLE = buildPolygonStyle({
  color: mapColors.crmScopedUatFill,
  fillOpacity: 0.6,
  lineColor: mapColors.crmScopedSelectedUnitLine,
  lineOpacity: 1,
  lineWidth: 3,
});

/** Static paint for an above-threshold overlay polygon. */
const THRESHOLD_ABOVE_STYLE = buildPolygonStyle({
  color: mapColors.crmScopedThresholdAboveFill,
  fillOpacity: 0.4,
  lineColor: mapColors.crmScopedThresholdAboveFill,
  lineOpacity: 1,
  lineWidth: 1,
});

/** Static paint for a below-threshold overlay polygon. */
const THRESHOLD_BELOW_STYLE = buildPolygonStyle({
  color: mapColors.crmScopedThresholdBelowFill,
  fillOpacity: 0.4,
  lineColor: mapColors.crmScopedThresholdBelowFill,
  lineOpacity: 1,
  lineWidth: 1,
});

/**
 * Mapbox-shaped `[[swLng, swLat], [neLng, neLat]]` bounding box over a
 * mixed list of GeoJSON geometries. Uses `@turf/bbox` so any geometry
 * type (Polygon, LineString, Point, Multi*) contributes.
 *
 * @param {GeoJSON.Geometry[]} geometries - Geometries to bound
 * @return {[[number, number], [number, number]] | null} Bounds or null
 */
const computeBounds = (
  geometries: GeoJSON.Geometry[]
): [[number, number], [number, number]] | null => {
  if (geometries.length === 0) return null;
  const [minLng, minLat, maxLng, maxLat] = bbox({
    type: 'FeatureCollection',
    features: geometries.map((geometry) => ({
      type: 'Feature',
      geometry,
      properties: null,
    })),
  });
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

/**
 * Selection-driven scoped map for the CRM Tract wizard. Used on
 * Current Development and Future Development steps.
 *
 * Scoped layer checkboxes are rendered in the Map sidebar via the
 * `scopedLayers` prop (same pattern as the packages page). General
 * layers (Wells, Development Areas, etc.) appear below under "General".
 *
 * @param {ScopedMapPanelProps} props - The component props.
 * @return {JSX.Element} The map panel.
 */
export const ScopedMapPanel = ({
  selectedUnitGeometry,
  wellFeatures,
  uatGeometries,
  subtractGeometry,
  selectedSlotId,
  selectedUatId,
  thresholdOverlay,
  allocationManager,
}: ScopedMapPanelProps): JSX.Element => {
  const horizontalWells = useAtomValue(horizontalWellsLayerAtom);
  const verticalWells = useAtomValue(verticalWellsLayerAtom);
  const horizontalUnits = useAtomValue(horizontalUnitsLayerAtom);
  const verticalUnits = useAtomValue(verticalUnitsLayerAtom);
  const uatLayerVisible = useAtomValue(unitAllocationTractsLayerAtom);
  const subtractVisible = useAtomValue(subtractLayerAtom);

  const wellsVisible = horizontalWells || verticalWells;
  const unitsVisible = horizontalUnits || verticalUnits;

  // Per-instance ref atom so each `<ScopedMapPanel>` mount owns its
  // own Mapbox ref slot.
  const [refAtom] = useState(() => atom<MapRef | null>(null));
  const mapRef = useAtomValue(refAtom);

  const center = useMemo(
    () =>
      computeCenter({ selectedUnitGeometry, subtractGeometry, uatGeometries }),
    [selectedUnitGeometry, subtractGeometry, uatGeometries]
  );

  // Re-frame the map on selection change.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;
    let bounds: [[number, number], [number, number]] | null = null;
    let padding = 80;
    if (thresholdOverlay && thresholdOverlay.items.length > 0) {
      bounds = computeBounds(thresholdOverlay.items.map((i) => i.geometry));
      padding = 40;
    } else if (selectedUnitGeometry) {
      bounds = computeBounds([selectedUnitGeometry]);
    } else if (subtractGeometry) {
      bounds = computeBounds([subtractGeometry]);
    } else if (uatGeometries && uatGeometries.length > 0) {
      bounds = computeBounds(uatGeometries.map((u) => u.geometry));
      padding = 40;
    }
    if (bounds) {
      map.fitBounds(bounds, { padding, maxZoom: 14, duration: 800 });
    }
  }, [
    mapRef,
    selectedUnitGeometry,
    subtractGeometry,
    uatGeometries,
    thresholdOverlay,
  ]);

  if (!center) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder}>Select a unit to view the map</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Map
        className={styles.map}
        longitude={center.longitude}
        latitude={center.latitude}
        mapConfigAtom={scopedMapConfigAtom}
        refAtom={refAtom}
        showControls
        showMapFilters
        scopedLayers={<ScopedCrmLayerSelection />}
      >
        {/* Subtract renders first so its Mapbox layer exists when other
            layers mount. Other layers use beforeId to stack below it —
            same pattern as allocation-tract-map.tsx. */}
        {subtractVisible && subtractGeometry && (
          <PolygonArea
            id="scoped-subtract"
            geometry={subtractGeometry}
            style={SUBTRACT_STYLE}
          />
        )}
        {unitsVisible && selectedUnitGeometry && (
          <PolygonArea
            id="scoped-selected-unit"
            geometry={selectedUnitGeometry}
            style={SELECTED_UNIT_STYLE}
            beforeId="polygon-fill-scoped-subtract"
          />
        )}
        {uatLayerVisible &&
          selectedUatId !== undefined &&
          uatGeometries
            ?.filter(({ id }) => id === selectedUatId)
            .map(({ id, geometry }) => (
              <PolygonArea
                key={`scoped-uat-${id}`}
                id={`scoped-uat-${id}`}
                geometry={geometry}
                style={UAT_SELECTED_STYLE}
                beforeId="polygon-fill-scoped-subtract"
              />
            ))}
        {wellsVisible &&
          wellFeatures?.map(({ uwi, feature }) => {
            if (!feature?.geometry) return null;
            const isSelected = uwi === selectedSlotId;
            if (feature.geometry.type === 'LineString') {
              return (
                <Line
                  key={`scoped-well-${uwi}`}
                  id={`scoped-well-${uwi}`}
                  geojson={feature}
                  style={
                    isSelected
                      ? SELECTED_SLOT_LINE_STYLE
                      : LINE_STYLES.BLACK_SOLID
                  }
                />
              );
            }
            return (
              <Point
                key={`scoped-well-${uwi}`}
                geojson={feature.geometry}
                className={isSelected ? styles.wellPointSelected : ''}
              />
            );
          })}
        {thresholdOverlay?.items.map(({ id, geometry, isAboveThreshold }) => (
          <PolygonArea
            key={`scoped-threshold-${id}`}
            id={`scoped-threshold-${id}`}
            geometry={geometry}
            style={
              isAboveThreshold ? THRESHOLD_ABOVE_STYLE : THRESHOLD_BELOW_STYLE
            }
            beforeId="polygon-fill-scoped-subtract"
          />
        ))}
      </Map>
      {allocationManager !== undefined && (
        <div className={styles.accordionDock}>{allocationManager}</div>
      )}
    </div>
  );
};
